/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {defineMessage, useIntl} from 'react-intl'
import {SimpleGrid, Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import useProfileFields from '../forms/useProfileFields'
import Field from '../field'
import PhoneField from '../phone-field'

/**
 * ProfileFields is a React functional component that renders a set of form fields
 * for user profile information. It utilizes the `useProfileFields` hook to manage
 * the form state and field configurations.
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.form - The form object that contains the state and methods
 *                              for managing form data.
 * @param {string} [props.prefix=''] - An optional prefix to be used for field names
 *                                     or IDs to ensure uniqueness.
 * @param {string} props.countryCode - The default country code to be used in the
 *                                     PhoneField component for formatting phone numbers.
 *
 * @returns {JSX.Element} A JSX element containing the structured form fields for
 *                        user profile information, including first name, last name,
 *                        email, phone number, and birthday.
 */
const ProfileFields = ({form, prefix = 'profile_', countryCode}) => {
    const fields = useProfileFields({form, prefix})
    const intl = useIntl()
    const formTitleAriaLabel = defineMessage({
        defaultMessage: 'Profile Form',
        id: 'profile_fields.label.profile_form'
    })
    const firstNameRef = useRef()

    useEffect(() => {
        if (firstNameRef.current) {
            firstNameRef.current.focus()
        }
    }, [])

    return (
        <Stack spacing={24} aria-label={intl.formatMessage(formTitleAriaLabel)}>
            <SimpleGrid columns={[1, 1, 1, 2]} spacing={24}>
                <Field {...fields.firstName} inputRef={firstNameRef} />
                <Field {...fields.lastName} />
            </SimpleGrid>
            <Field {...fields.email} isDisabled />
            <SimpleGrid columns={[1, 1, 1, 2]} spacing={24}>
                <PhoneField {...fields.phone} defaultCountry={countryCode} />
                <Field {...fields.birthday} />
            </SimpleGrid>
        </Stack>
    )
}

ProfileFields.propTypes = {
    /** Object returned from `useForm` */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Country code for the phone field */
    countryCode: PropTypes.string
}

export default ProfileFields
